import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Link as MuiLink
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  AttachMoney as AttachMoneyIcon,
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Receipt as ReceiptIcon,
  School as SchoolIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  Download as DownloadIcon,
  ReceiptLong as ReceiptLongIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon
} from '@mui/icons-material';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { teacherPaymentService } from '../../services/teacherPaymentService';
import dayjs from 'dayjs';

// Interfaz para el desglose de pago
interface PaymentBreakdown {
  regular_hours: number;
  regular_rate: number;
  regular_amount: number;
  extra_hours: number;
  extra_rate: number;
  extra_amount: number;
  total_hours: number;
  total_amount: number;
  bonus?: number;
  deductions?: number;
  net_amount: number;
}

const TeacherPaymentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const [payment, setPayment] = useState<any>(null);
  const [breakdown, setBreakdown] = useState<PaymentBreakdown | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [proofDialogOpen, setProofDialogOpen] = useState(false);

  useEffect(() => {
    loadPayment();
  }, [id]);

  const loadPayment = async () => {
    try {
      setLoading(true);
      const response = await teacherPaymentService.getById(parseInt(id!));
      const paymentData = response.data;
      setPayment(paymentData);
      
      // Calcular desglose de pago
      calculateBreakdown(paymentData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar pago');
    } finally {
      setLoading(false);
    }
  };

  const calculateBreakdown = (paymentData: any) => {
    // Horas regulares
    const regularHours = paymentData.hours_worked || 0;
    const regularRate = paymentData.hourly_rate || 0;
    const regularAmount = regularHours * regularRate;
    
    // Horas extras
    const extraHours = paymentData.hours_extra || 0;
    const extraRate = paymentData.hourly_rate_extra || 500;
    const extraAmount = extraHours * extraRate;
    
    // Totales
    const totalHours = parseFloat(regularHours) + parseFloat(extraHours);
    const totalAmount = regularAmount + extraAmount;
    
    // Bonificaciones y deducciones (si existen)
    const bonus = paymentData.bonus || 0;
    const deductions = paymentData.deductions || 0;
    const netAmount = totalAmount + bonus - deductions;
    
    setBreakdown({
      regular_hours: regularHours,
      regular_rate: regularRate,
      regular_amount: regularAmount,
      extra_hours: extraHours,
      extra_rate: extraRate,
      extra_amount: extraAmount,
      total_hours: totalHours,
      total_amount: totalAmount,
      bonus: bonus,
      deductions: deductions,
      net_amount: netAmount
    });
  };

  const handleDelete = async () => {
    try {
      setActionLoading(true);
      await teacherPaymentService.delete(parseInt(id!));
      navigate('/teacher-payments');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al eliminar pago');
      setActionLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleMarkAsPaid = async () => {
    try {
      setActionLoading(true);
      await teacherPaymentService.markAsPaid(parseInt(id!));
      await loadPayment();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al marcar como pagado');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePrintReceipt = () => {
    window.print();
  };

  const handleSendEmail = async () => {
    try {
      setEmailSending(true);
      await teacherPaymentService.sendReceiptByEmail(parseInt(id!));
      alert('Recibo enviado exitosamente al profesor');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al enviar recibo por email');
    } finally {
      setEmailSending(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setPdfGenerating(true);
      await teacherPaymentService.downloadPDF(
        parseInt(id!), 
        `pago-${payment.teacher?.first_name}-${payment.month}-${payment.year}.pdf`
      );
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al generar PDF');
    } finally {
      setPdfGenerating(false);
    }
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'paid':
        return { label: 'Pagado', color: 'success' as const, icon: <CheckCircleIcon /> };
      case 'pending':
        return { label: 'Pendiente', color: 'warning' as const, icon: <ErrorIcon /> };
      case 'cancelled':
        return { label: 'Cancelado', color: 'error' as const, icon: <WarningIcon /> };
      default:
        return { label: status, color: 'default' as const, icon: <ReceiptIcon /> };
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !payment) {
    return (
      <Box m={2}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={() => navigate('/teacher-payments')}>
            Volver a pagos
          </Button>
        }>
          {error || 'Pago no encontrado'}
        </Alert>
      </Box>
    );
  }

  const statusInfo = getStatusInfo(payment.status);

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
          <IconButton component={Link} to="/teacher-payments" sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Avatar sx={{ width: 56, height: 56, bgcolor: 'secondary.main' }}>
            <PersonIcon fontSize="large" />
          </Avatar>
          <Box>
            <Typography variant="h4" component="h1">
              {payment.teacher?.first_name} {payment.teacher?.last_name}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {payment.month} {payment.year}
            </Typography>
          </Box>
          <Chip
            icon={statusInfo.icon}
            label={statusInfo.label}
            color={statusInfo.color}
            sx={{ ml: { xs: 0, sm: 2 }, mt: { xs: 1, sm: 0 } }}
          />
        </Box>
        
        <Box display="flex" flexWrap="wrap" gap={1}>
          <Tooltip title="Imprimir recibo">
            <IconButton onClick={handlePrintReceipt}>
              <PrintIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Enviar por email">
            <IconButton 
              onClick={handleSendEmail} 
              disabled={emailSending}
            >
              {emailSending ? <CircularProgress size={24} /> : <EmailIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Descargar PDF">
            <IconButton 
              onClick={handleDownloadPDF} 
              disabled={pdfGenerating}
            >
              {pdfGenerating ? <CircularProgress size={24} /> : <DownloadIcon />}
            </IconButton>
          </Tooltip>
          
          {payment.status === 'pending' && (
            <Button
              variant="contained"
              color="success"
              onClick={handleMarkAsPaid}
              disabled={actionLoading}
            >
              {actionLoading ? <CircularProgress size={24} /> : 'Marcar como Pagado'}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/teacher-payments/${id}/edit`)}
            disabled={!['admin', 'manager'].includes(user?.role || '') || actionLoading}
          >
            Editar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => setDeleteDialogOpen(true)}
            disabled={!['admin', 'manager'].includes(user?.role || '')}
          >
            Eliminar
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Información del Profesor */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Información del Profesor
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" sx={{ width: '40%', bgcolor: 'action.hover' }}>
                        Nombre Completo
                      </TableCell>
                      <TableCell>
                        {payment.teacher?.first_name} {payment.teacher?.last_name}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                        Especialización
                      </TableCell>
                      <TableCell>
                        {payment.teacher?.specialization || 'No especificada'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                        Email
                      </TableCell>
                      <TableCell>
                        {payment.teacher?.email ? (
                          <MuiLink href={`mailto:${payment.teacher.email}`}>
                            {payment.teacher.email}
                          </MuiLink>
                        ) : 'No especificado'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                        Teléfono
                      </TableCell>
                      <TableCell>
                        {payment.teacher?.phone || 'No especificado'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                        Tarifa por Hora Regular
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<AttachMoneyIcon />}
                          label={formatCurrency(payment.hourly_rate || 0)}
                          color="primary"
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                        Tarifa por Hora Extra
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<TrendingUpIcon />}
                          label={formatCurrency(payment.hourly_rate_extra || 500)}
                          color="secondary"
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Detalles del Pago */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Detalles del Pago
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" sx={{ width: '40%', bgcolor: 'action.hover' }}>
                        Período
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          {payment.month} {payment.year}
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                        Fecha de Pago
                      </TableCell>
                      <TableCell>
                        {payment.payment_date 
                          ? dayjs(payment.payment_date).format('dddd, D [de] MMMM [de] YYYY')
                          : 'No procesado'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                        Estado
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={statusInfo.icon}
                          label={statusInfo.label}
                          color={statusInfo.color}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                    {payment.bank_reference && (
                      <TableRow>
                        <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                          Referencia Bancaria
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {payment.bank_reference}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {payment.payment_proof_url && (
                      <TableRow>
                        <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                          Comprobante
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ReceiptLongIcon />}
                            onClick={() => setProofDialogOpen(true)}
                          >
                            Ver comprobante
                          </Button>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Desglose de Pago DETALLADO */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Desglose de Pago
              </Typography>
              
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableBody>
                    {/* Horas Regulares */}
                    <TableRow>
                      <TableCell component="th" sx={{ width: '40%', bgcolor: 'action.hover' }}>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="medium">
                            Horas Regulares
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Clases regulares del período
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1">
                          {breakdown?.regular_hours || 0} horas × {formatCurrency(breakdown?.regular_rate || 0)}
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {formatCurrency(breakdown?.regular_amount || 0)}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Horas Extras */}
                    {(breakdown?.extra_hours || 0) > 0 && (
                      <TableRow>
                        <TableCell component="th" sx={{ bgcolor: 'info.50' }}>
                          <Box>
                            <Typography variant="subtitle1" fontWeight="medium" color="info.main">
                              Horas Extras
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Clases adicionales o suplencias
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body1">
                            {breakdown?.extra_hours || 0} horas × {formatCurrency(breakdown?.extra_rate || 0)}
                          </Typography>
                          <Typography variant="h6" color="info.main">
                            + {formatCurrency(breakdown?.extra_amount || 0)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}

                    {/* Subtotal */}
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell component="th" sx={{ bgcolor: 'grey.100' }}>
                        <Typography variant="subtitle1" fontWeight="medium">
                          Subtotal
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {breakdown?.total_hours || 0} horas totales
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="h6">
                          {formatCurrency(breakdown?.total_amount || 0)}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Bonificaciones (si existen) */}
                    {(breakdown?.bonus || 0) > 0 && (
                      <TableRow>
                        <TableCell component="th" sx={{ bgcolor: 'success.50' }}>
                          <Box display="flex" alignItems="center">
                            <TrendingUpIcon sx={{ mr: 1, color: 'success.main' }} />
                            <Typography variant="subtitle1" fontWeight="medium" color="success.main">
                              Bonificaciones
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            Rendimiento, puntualidad, etc.
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'success.main' }}>
                          <Typography variant="h6">
                            + {formatCurrency(breakdown?.bonus || 0)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}

                    {/* Deducciones (si existen) */}
                    {(breakdown?.deductions || 0) > 0 && (
                      <TableRow>
                        <TableCell component="th" sx={{ bgcolor: 'error.50' }}>
                          <Box display="flex" alignItems="center">
                            <TrendingDownIcon sx={{ mr: 1, color: 'error.main' }} />
                            <Typography variant="subtitle1" fontWeight="medium" color="error.main">
                              Deducciones
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            Faltas, retardos, etc.
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'error.main' }}>
                          <Typography variant="h6">
                            - {formatCurrency(breakdown?.deductions || 0)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}

                    {/* Línea separadora */}
                    <TableRow>
                      <TableCell colSpan={2}>
                        <Divider sx={{ my: 1 }} />
                      </TableCell>
                    </TableRow>

                    {/* TOTAL NETO */}
                    <TableRow sx={{ bgcolor: 'primary.50' }}>
                      <TableCell component="th">
                        <Typography variant="h5" fontWeight="bold" color="primary">
                          TOTAL NETO A PAGAR
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Monto final después de cálculos
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="h4" color="primary" fontWeight="bold">
                          {formatCurrency(breakdown?.net_amount || breakdown?.total_amount || payment.total_amount || 0)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Resumen Rápido en Tarjetas */}
        <Grid size={{ xs: 12 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Card sx={{ bgcolor: 'info.50' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <AccessTimeIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                  <Typography variant="h4">
                    {breakdown?.total_hours || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Horas Totales
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {breakdown?.regular_hours || 0} reg + {breakdown?.extra_hours || 0} extra
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Card sx={{ bgcolor: 'success.50' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <AttachMoneyIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                  <Typography variant="h5">
                    {formatCurrency(breakdown?.regular_rate || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tarifa Regular
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    por hora
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Card sx={{ bgcolor: 'warning.50' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <TrendingUpIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                  <Typography variant="h5">
                    {formatCurrency(breakdown?.extra_rate || 500)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tarifa Extra
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    por hora extra
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Card sx={{ bgcolor: 'primary.50' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <ReceiptLongIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h5">
                    {formatCurrency(breakdown?.net_amount || breakdown?.total_amount || payment.total_amount || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total a Pagar
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    neto
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Notas e Información Adicional */}
        {payment.description && (
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Notas Adicionales
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="body1">
                    {payment.description}
                  </Typography>
                </Paper>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Información de Seguimiento */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Información de Seguimiento
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Creado por
                  </Typography>
                  <Typography>
                    {payment.user?.name || 'Sistema'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {dayjs(payment.created_at).format('DD/MM/YYYY HH:mm')}
                  </Typography>
                </Grid>
                
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Última actualización
                  </Typography>
                  <Typography>
                    {payment.updated_at ? dayjs(payment.updated_at).format('DD/MM/YYYY HH:mm') : 'N/A'}
                  </Typography>
                </Grid>
                
                {payment.status === 'paid' && payment.payment_date && (
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Fecha de Pago Real
                    </Typography>
                    <Typography>
                      {dayjs(payment.payment_date).format('DD/MM/YYYY')}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Diálogo de Confirmación de Eliminación */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro que deseas eliminar este pago?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta acción no se puede deshacer.
          </Typography>
          {payment?.status === 'paid' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Este pago ya fue procesado y registrado en el balance general.
              Al eliminarlo, se revertirá automáticamente el balance.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo para ver comprobante */}
      <Dialog
        open={proofDialogOpen}
        onClose={() => setProofDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Comprobante de Pago</Typography>
            <IconButton onClick={() => setProofDialogOpen(false)}>
              <ArrowBackIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {payment?.payment_proof_url && (
            <Box sx={{ textAlign: 'center' }}>
              {payment.payment_proof_url.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                <Box
                  component="img"
                  src={payment.payment_proof_url}
                  alt="Comprobante"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '80vh',
                    objectFit: 'contain'
                  }}
                />
              ) : (
                <Box sx={{ height: '70vh', width: '100%' }}>
                  <iframe
                    src={payment.payment_proof_url}
                    title="Comprobante PDF"
                    width="100%"
                    height="100%"
                    style={{ border: 'none' }}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<DownloadIcon />}
            href={payment?.payment_proof_url}
            download
          >
            Descargar
          </Button>
          <Button onClick={() => setProofDialogOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeacherPaymentDetail;